import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button, Dropdown, Form, InputGroup, Spinner } from 'react-bootstrap';
import { BsCheck2, BsChevronDown, BsCollection, BsFolder2Open, BsGlobe2, BsPerson, BsSearch, BsX } from 'react-icons/bs';
import { getSetCardFolders, getSetPickerCards } from '../api/setsApi';
import './SetCardPicker.css';

const CLASS_OPTIONS = [
  { id: 'all', label: 'All classes' },
  { id: 'deathknight', label: 'Death Knight' },
  { id: 'demonhunter', label: 'Demon Hunter' },
  { id: 'druid', label: 'Druid' },
  { id: 'hunter', label: 'Hunter' },
  { id: 'mage', label: 'Mage' },
  { id: 'paladin', label: 'Paladin' },
  { id: 'priest', label: 'Priest' },
  { id: 'rogue', label: 'Rogue' },
  { id: 'shaman', label: 'Shaman' },
  { id: 'warlock', label: 'Warlock' },
  { id: 'warrior', label: 'Warrior' },
  { id: 'neutral', label: 'Neutral' },
  { id: 'custom', label: 'Custom classes', customOnly: true },
];

const cardSource = (card) => card?.source === 'ingame' || Number(card?.type) === 1 ? 'ingame' : 'custom';
const cardKey = (card) => `${cardSource(card)}:${cardSource(card) === 'ingame' ? card.cardid || card.id : card.id}`;
const normalizeCard = (card) => ({ ...card, source: cardSource(card), type: cardSource(card) === 'ingame' ? 1 : 0, quantity: Math.max(1, Number(card.quantity) || 1) });
const cardImage = (card) => cardSource(card) === 'ingame'
  ? `https://art.hearthstonejson.com/v1/render/latest/enUS/256x/${encodeURIComponent(card.cardid)}.png`
  : `https://www.hearthcards.net/cards/${card.cardid}.webp`;
const normalizedClass = (value) => String(value || '').toLowerCase().replace(/[\s_-]/g, '');

function ClassFilter({ value, source, onChange }) {
  const options = CLASS_OPTIONS.filter((option) => !option.customOnly || source === 'custom');
  const selected = options.find((option) => option.id === value) || options[0];
  return (
    <Dropdown className="set-picker-class-filter">
      <Dropdown.Toggle variant="light" size="sm">
        {selected.id === 'all' ? <BsCollection /> : selected.customOnly ? <span className="set-picker-custom-class">C</span> : <img src={`/images/class-icons/${selected.id}.png`} alt="" />}
        <span>{selected.label}</span><BsChevronDown />
      </Dropdown.Toggle>
      <Dropdown.Menu>
        {options.map((option) => <Dropdown.Item key={option.id} active={option.id === value} onClick={() => onChange(option.id)}>
          {option.id === 'all' ? <BsCollection /> : option.customOnly ? <span className="set-picker-custom-class">C</span> : <img src={`/images/class-icons/${option.id}.png`} alt="" />}
          <span>{option.label}</span>
        </Dropdown.Item>)}
      </Dropdown.Menu>
    </Dropdown>
  );
}

function CardTile({ card, selected, deckMode, pendingRemoval, onToggle }) {
  return (
    <button type="button" className={`set-picker-card ${selected ? 'set-is-selected' : ''}`} onClick={() => onToggle(card)} aria-pressed={Boolean(selected)}>
      <span className="set-picker-card-image">
        <img src={cardImage(card)} alt={card.cardname || 'Card'} loading="lazy" />
        {selected && <span className="set-picker-selected-mark"><BsCheck2 /></span>}
        {selected && deckMode && <span className="set-picker-quantity">×{selected.quantity}</span>}
        {cardSource(card) === 'ingame' && <span className="set-picker-source-mark set-is-ingame">Game</span>}
        {pendingRemoval && <span className="set-picker-removal"><strong>Removing…</strong><small>Click to undo</small></span>}
      </span>
      <span className="set-picker-card-name">{card.cardname || card.cardid}</span>
    </button>
  );
}

export default function SetCardPicker({ onConfirm, onClose, limit = 15, preSelectedCards = [], deckCardMode = false, pickerMode = 'cards' }) {
  const searchRef = useRef(null);
  const removalTimersRef = useRef(new Map());
  const [source, setSource] = useState('custom');
  const [view, setView] = useState('browse');
  const [cards, setCards] = useState([]);
  const [selectedCards, setSelectedCards] = useState(() => preSelectedCards.map(normalizeCard));
  const [folders, setFolders] = useState([]);
  const [folder, setFolder] = useState('all');
  const [classFilter, setClassFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [pagination, setPagination] = useState({ page: 1, total: 0, hasMore: false });
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [pendingRemovalKeys, setPendingRemovalKeys] = useState(() => new Set());

  const selectionMap = useMemo(() => new Map(selectedCards.map((card) => [cardKey(card), card])), [selectedCards]);
  const totalQuantity = useMemo(() => selectedCards.reduce((total, card) => total + (Number(card.quantity) || 1), 0), [selectedCards]);
  const selectionCount = deckCardMode ? totalQuantity : selectedCards.length;
  const limitLabel = deckCardMode ? 'total deck cards' : 'cards';
  const shownSelectedCards = useMemo(() => selectedCards.filter((card) => {
    const matchesSearch = !search.trim() || String(card.cardname || '').toLowerCase().includes(search.trim().toLowerCase());
    if (!matchesSearch || classFilter === 'all') return matchesSearch;
    if (classFilter === 'custom') return /^(hc|ug)_/i.test(String(card.class || ''));
    return normalizedClass(card.class) === classFilter;
  }), [classFilter, search, selectedCards]);

  useEffect(() => {
    document.body.classList.add('set-card-picker-open');
    const escape = (event) => { if (event.key === 'Escape') onClose(); };
    window.addEventListener('keydown', escape);
    window.setTimeout(() => searchRef.current?.focus(), 0);
    return () => {
      document.body.classList.remove('set-card-picker-open');
      window.removeEventListener('keydown', escape);
      removalTimersRef.current.forEach((timer) => window.clearTimeout(timer));
      removalTimersRef.current.clear();
    };
  }, [onClose]);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(search.trim()), 280);
    return () => window.clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    const controller = new AbortController();
    getSetCardFolders(controller.signal).then((payload) => setFolders(payload.folders || []))
      .catch((loadError) => { if (loadError.name !== 'AbortError') setError(loadError.message); });
    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (source === 'ingame' && classFilter === 'custom') setClassFilter('all');
  }, [source]);

  useEffect(() => {
    if (view !== 'browse') return undefined;
    const controller = new AbortController();
    setLoading(true); setError(''); setNotice('');
    getSetPickerCards({ source, mode: pickerMode, folder: source === 'custom' ? folder : undefined, class: classFilter, search: debouncedSearch, page: 1 }, controller.signal)
      .then((payload) => { setCards(payload.cards || []); setPagination(payload.pagination || { page: 1, total: 0, hasMore: false }); })
      .catch((loadError) => { if (loadError.name !== 'AbortError') { setCards([]); setError(loadError.message); } })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [classFilter, debouncedSearch, folder, pickerMode, source, view]);

  const cancelPendingRemoval = (key, reduceDeckQuantity = false) => {
    const timer = removalTimersRef.current.get(key);
    if (timer) window.clearTimeout(timer);
    removalTimersRef.current.delete(key);
    setPendingRemovalKeys((current) => { const next = new Set(current); next.delete(key); return next; });
    if (reduceDeckQuantity) setSelectedCards((current) => current.map((card) => cardKey(card) === key ? { ...card, quantity: 1 } : card));
  };

  const scheduleRemoval = (key) => {
    if (removalTimersRef.current.has(key)) return;
    setPendingRemovalKeys((current) => new Set(current).add(key));
    const timer = window.setTimeout(() => {
      setSelectedCards((current) => current.filter((card) => cardKey(card) !== key));
      setPendingRemovalKeys((current) => { const next = new Set(current); next.delete(key); return next; });
      removalTimersRef.current.delete(key);
    }, 1000);
    removalTimersRef.current.set(key, timer);
  };

  const toggleCard = (rawCard) => {
    const card = normalizeCard(rawCard);
    const key = cardKey(card);
    const existing = selectionMap.get(key);
    setNotice('');
    if (pendingRemovalKeys.has(key)) {
      cancelPendingRemoval(key, deckCardMode && Number(existing?.quantity) === 2);
      return;
    }
    if (!existing && limit !== 1 && selectionCount >= limit) {
      setNotice(`You can select up to ${limit} ${limitLabel}.`);
      return;
    }
    if (existing) {
      if (deckCardMode && Number(existing.quantity || 1) < 2) {
        if (selectionCount >= limit) {
          if (view === 'selected') scheduleRemoval(key);
          else setNotice(`You can select up to ${limit} ${limitLabel}.`);
          return;
        }
        setSelectedCards((current) => current.map((item) => cardKey(item) === key ? { ...item, quantity: 2 } : item));
      } else if (view === 'selected') {
        scheduleRemoval(key);
      } else {
        setSelectedCards((current) => current.filter((item) => cardKey(item) !== key));
      }
      return;
    }
    if (limit === 1) setSelectedCards([{ ...card, quantity: 1 }]);
    else setSelectedCards((current) => {
      const currentCount = deckCardMode
        ? current.reduce((total, item) => total + (Number(item.quantity) || 1), 0)
        : current.length;
      return currentCount >= limit ? current : [...current, { ...card, quantity: 1 }];
    });
  };

  const addCards = (incoming) => {
    setSelectedCards((current) => {
      const next = [...current];
      const known = new Set(next.map(cardKey));
      let count = deckCardMode
        ? next.reduce((total, card) => total + (Number(card.quantity) || 1), 0)
        : next.length;
      for (const rawCard of incoming) {
        if (count >= limit) break;
        const card = normalizeCard(rawCard);
        if (known.has(cardKey(card))) continue;
        next.push({ ...card, quantity: 1 });
        known.add(cardKey(card));
        count++;
      }
      return next;
    });
  };

  const loadMore = async () => {
    if (!pagination.hasMore || loadingMore) return;
    setLoadingMore(true); setError('');
    try {
      const payload = await getSetPickerCards({ source, mode: pickerMode, folder: source === 'custom' ? folder : undefined, class: classFilter, search: debouncedSearch, page: pagination.page + 1 });
      setCards((current) => [...current, ...(payload.cards || [])]);
      setPagination(payload.pagination || pagination);
    } catch (loadError) { setError(loadError.message); }
    finally { setLoadingMore(false); }
  };

  const addFolder = async () => {
    if (source !== 'custom' || bulkBusy) return;
    setBulkBusy(true); setError(''); setNotice('');
    try {
      const payload = await getSetPickerCards({ source: 'custom', mode: pickerMode, folder, bulk: 1, limit });
      const incoming = payload.cards || [];
      addCards(incoming);
      if (payload.pagination?.truncated || incoming.length > Math.max(0, limit - selectionCount)) {
        setNotice(`Added as many cards as the ${limit} ${limitLabel} limit allows.`);
      }
    } catch (loadError) { setError(loadError.message); }
    finally { setBulkBusy(false); }
  };

  const confirm = () => { onConfirm(selectedCards.filter((card) => !pendingRemovalKeys.has(cardKey(card)))); onClose(); };
  const openSelected = () => {
    setSearch('');
    setDebouncedSearch('');
    setClassFilter('all');
    setView('selected');
  };
  const visibleCards = view === 'selected' ? shownSelectedCards : cards;
  const activeFolder = folders.find((item) => String(item.id) === String(folder));
  const portalTarget = document.querySelector('.set-module-editor-modal')?.closest('.modal') || document.body;

  return createPortal(
    <div className="set-card-picker-layer" role="presentation">
      <button type="button" className="set-card-picker-backdrop" onClick={onClose} aria-label="Close card picker" />
      <aside className="set-card-picker" role="dialog" aria-modal="true" aria-labelledby="set-card-picker-title">
        <header className="set-card-picker-header">
          <h2 id="set-card-picker-title">Choose Card</h2>
          <button type="button" onClick={onClose} aria-label="Close"><BsX /></button>
        </header>

        <div className="set-card-picker-source" role="tablist" aria-label="Card source">
          <button type="button" role="tab" aria-selected={source === 'custom'} className={source === 'custom' ? 'set-is-active' : ''} onClick={() => { setSource('custom'); setView('browse'); }}><BsPerson /><span>My cards</span></button>
          <button type="button" role="tab" aria-selected={source === 'ingame'} className={source === 'ingame' ? 'set-is-active' : ''} onClick={() => { setSource('ingame'); setView('browse'); }}><BsGlobe2 /><span>Game cards</span></button>
        </div>

        <div className="set-card-picker-tools">
          <InputGroup size="sm" className="set-picker-search"><InputGroup.Text><BsSearch /></InputGroup.Text><Form.Control ref={searchRef} value={search} onChange={(event) => setSearch(event.target.value)} placeholder={view === 'selected' ? 'Search selected cards' : 'Search cards'} aria-label="Search cards" /></InputGroup>
          <div className="set-picker-filters">
            <ClassFilter value={classFilter} source={source} onChange={setClassFilter} />
            {source === 'custom' && <Form.Select size="sm" value={folder} onChange={(event) => setFolder(event.target.value)} aria-label="Card folder">{folders.map((item) => <option key={item.id} value={item.id}>{item.name} ({item.count})</option>)}</Form.Select>}
          </div>
          {source === 'custom' && view === 'browse' && activeFolder && !activeFolder.special && <Button size="sm" variant="outline-secondary" className="set-picker-folder-add" onClick={addFolder} disabled={bulkBusy || selectionCount >= limit}>{bulkBusy ? <Spinner size="sm" /> : <BsFolder2Open />} Add all</Button>}
        </div>

        <div className="set-card-picker-view-tabs">
          <button type="button" className={view === 'browse' ? 'set-is-active' : ''} onClick={() => setView('browse')}>Browse <span>{pagination.total}</span></button>
          <button type="button" className={view === 'selected' ? 'set-is-active' : ''} onClick={openSelected}>Selected <span>{selectedCards.length}</span></button>
        </div>

        <div className="set-card-picker-content">
          {error && <div className="set-picker-message set-is-error">{error}</div>}
          {notice && <div className="set-picker-message">{notice}</div>}
          {loading && view === 'browse' ? <div className="set-picker-loading"><Spinner animation="border" /><span>Loading cards…</span></div> : visibleCards.length > 0 ? (
            <div className="set-picker-card-grid">
              {visibleCards.map((card) => <CardTile key={cardKey(card)} card={card} selected={selectionMap.get(cardKey(card))} deckMode={deckCardMode} pendingRemoval={pendingRemovalKeys.has(cardKey(card))} onToggle={toggleCard} />)}
            </div>
          ) : <div className="set-picker-empty"><BsCollection /><strong>{view === 'selected' ? 'No selected cards match' : 'No cards found'}</strong><span>{view === 'selected' ? 'Change the filters or return to Browse.' : 'Try another folder, class, or search.'}</span></div>}
          {view === 'browse' && pagination.hasMore && !loading && <Button variant="outline-secondary" className="set-picker-load-more" onClick={loadMore} disabled={loadingMore}>{loadingMore && <Spinner size="sm" />} Load more</Button>}
        </div>

        <footer className="set-card-picker-footer">
          <div><strong>{selectionCount}</strong><span>{deckCardMode ? `of ${limit} total cards` : `of ${limit} selected`}</span></div>
          <Button variant="light" onClick={onClose}>Cancel</Button>
          <Button onClick={confirm}>Use selected cards</Button>
        </footer>
      </aside>
    </div>,
    portalTarget,
  );
}
